import React, { useState, useEffect, useRef } from 'react'
import { useSearchParams, useLocation } from 'react-router-dom'
import { useHospitalSelection } from '../../hooks/useHospitalSelection'
import './HospitalSelector.css'

interface Hospital {
    id: string
    name: string
}

const HospitalSelector: React.FC = () => {
    const [searchParams] = useSearchParams()
    const location = useLocation()
    const { selectedHospitalIds, setSelectedHospitalIds } = useHospitalSelection()
    const [hospitals, setHospitals] = useState<Hospital[]>([])
    const [loading, setLoading] = useState(true)
    const [isOpen, setIsOpen] = useState(false)
    const [tempSelected, setTempSelected] = useState<string[]>([])
    const [searchTerm, setSearchTerm] = useState('')
    const [highlightedIndex, setHighlightedIndex] = useState(-1)
    const [dropdownTop, setDropdownTop] = useState<number | null>(null)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const headerRef = useRef<HTMLDivElement>(null)
    const lastFetchedIdsRef = useRef<string>('')

    const isTmbOrPmh = React.useMemo(() => {
        const path = location.pathname.toLowerCase()
        return path.includes('/tmb') || path.includes('/pmh')
    }, [location.pathname])

    const isBcbs = React.useMemo(() => {
        return location.pathname.toLowerCase().includes('/bcbs')
    }, [location.pathname])

    const accessibleHospitalIds = React.useMemo(() => {
        const urlParam = searchParams.get('HospitalId')
        if (urlParam) {
            const cleanParam = urlParam.startsWith('(') && urlParam.endsWith(')')
                ? urlParam.slice(1, -1)
                : urlParam
            return cleanParam.split(',').map(id => id.trim()).filter(id => id.length > 0)
        }
        return []
    }, [searchParams])

    useEffect(() => {
        if (accessibleHospitalIds.length === 0) {
            setSelectedHospitalIds([])
            sessionStorage.removeItem('selectedHospitalIds')
            sessionStorage.removeItem('hospitalNames')
            setHospitals([])
            setLoading(false)
            lastFetchedIdsRef.current = ''
            return
        }

        const idsKey = `${isTmbOrPmh ? 'tmb:' : isBcbs ? 'bcbs:' : 'huddle:'}${accessibleHospitalIds.join(',')}`
        if (lastFetchedIdsRef.current === idsKey) return

        const fetchUrl = isTmbOrPmh
            ? `/api/tmb/hospitals/${accessibleHospitalIds.join(',')}`
            : isBcbs
                ? `/api/bcbs/hospitals/${accessibleHospitalIds.join(',')}`
                : `/api/hospitals/${accessibleHospitalIds.join(',')}`

        lastFetchedIdsRef.current = idsKey

        fetch(fetchUrl)
            .then((res) => res.text())
            .then((rawData) => {
                const sanitized = rawData.replace(/\\'/g, "'")
                const jsonData = JSON.parse(sanitized)
                const rows = jsonData?.response?.result?.rows || []

                const seenIds = new Set<string>()
                const hospitalList: Hospital[] = rows.map((row: any[]) => {
                    let hospitalIdValue = String(row[0])
                    if ((isTmbOrPmh || isBcbs) && row[2]) {
                        const match = String(row[2]).match(/^(\d{3})/)
                        if (match) {
                            hospitalIdValue = match[1]
                        }
                    }
                    return {
                        id: hospitalIdValue,
                        name: String(row[2]),
                    }
                }).filter((h: Hospital) => {
                    if (seenIds.has(h.id)) return false
                    seenIds.add(h.id)
                    return true
                })

                setHospitals(hospitalList)

                let savedIds: string[] = []
                try {
                    const stored = sessionStorage.getItem('selectedHospitalIds')
                    savedIds = stored ? JSON.parse(stored) : []
                } catch {
                    savedIds = []
                }

                const validHospitalIds = hospitalList.map(h => h.id)
                const filteredSavedIds = savedIds.filter(id => validHospitalIds.includes(id))

                if (!filteredSavedIds || filteredSavedIds.length === 0) {
                    const userCleared = sessionStorage.getItem('userClearedSelection') === 'true'
                    if (!userCleared && hospitalList.length > 0) {
                        setSelectedHospitalIds([hospitalList[0].id])
                        const namesMap: Record<string, string> = {}
                        namesMap[hospitalList[0].id] = hospitalList[0].name
                        sessionStorage.setItem('hospitalNames', JSON.stringify(namesMap))
                    }
                } else {
                    const idsToSelect = filteredSavedIds.length > 0 ? filteredSavedIds : (hospitalList.length > 0 ? [hospitalList[0].id] : [])

                    if (idsToSelect.length > 0) {
                        const isSameData = selectedHospitalIds.length === idsToSelect.length &&
                            selectedHospitalIds.every(id => idsToSelect.includes(id))

                        if (!isSameData) {
                            setSelectedHospitalIds(idsToSelect)
                        }

                        const namesMap: Record<string, string> = {}
                        idsToSelect.forEach(id => {
                            const hospital = hospitalList.find(h => h.id === id)
                            if (hospital) {
                                namesMap[id] = hospital.name
                            }
                        })
                        sessionStorage.setItem('hospitalNames', JSON.stringify(namesMap))
                    }
                }
            })

            .catch(() => { })
            .finally(() => {
                setLoading(false)
            })
    }, [accessibleHospitalIds, setSelectedHospitalIds, isTmbOrPmh])



    useEffect(() => {
        if (isOpen) {
            setTempSelected([...selectedHospitalIds])
            setSearchTerm('')
            setHighlightedIndex(-1)
        }
    }, [isOpen, selectedHospitalIds])

    useEffect(() => {
        if (highlightedIndex < 0 || !dropdownRef.current) return
        const list = dropdownRef.current.querySelector('.dropdown-list') as HTMLElement | null
        if (!list) return
        const el = list.querySelectorAll('.hospital-option')[highlightedIndex] as HTMLElement | undefined
        if (!el) return

        const searchEl = list.querySelector('.hospital-search-input') as HTMLElement | null
        const footerEl = list.querySelector('.dropdown-footer-buttons') as HTMLElement | null
        const stickyTop = searchEl ? searchEl.offsetHeight : 0
        const stickyBottom = footerEl ? footerEl.offsetHeight : 0

        const elTop = el.offsetTop
        const elBottom = elTop + el.offsetHeight
        const viewTop = list.scrollTop + stickyTop
        const viewBottom = list.scrollTop + list.clientHeight - stickyBottom

        if (elTop < viewTop) {
            list.scrollTop = elTop - stickyTop
        } else if (elBottom > viewBottom) {
            list.scrollTop = elBottom - list.clientHeight + stickyBottom
        }
    }, [highlightedIndex])

    const filteredHospitals = React.useMemo(() => {
        const term = searchTerm.trim().toLowerCase()
        if (!term) return hospitals
        return hospitals.filter(h =>
            h.name.toLowerCase().includes(term) || h.id.toLowerCase().includes(term)
        )
    }, [hospitals, searchTerm])

    useEffect(() => {
        if (isOpen && headerRef.current && window.innerWidth <= 480) {
            const rect = headerRef.current.getBoundingClientRect()
            setDropdownTop(rect.bottom + 5)
        } else {
            setDropdownTop(null)
        }
    }, [isOpen])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false)
            }
        }

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isOpen])

    const handleToggleAll = () => {
        const visibleIds = filteredHospitals.map((h) => h.id)
        if (visibleIds.length === 0) return
        const allVisibleSelected = visibleIds.every(id => tempSelected.includes(id))
        if (allVisibleSelected) {
            setTempSelected(prev => prev.filter(id => !visibleIds.includes(id)))
        } else {
            setTempSelected(prev => [...new Set([...prev, ...visibleIds])])
            sessionStorage.removeItem('userClearedSelection')
        }
    }

    const handleToggleHospital = (id: string) => {
        setTempSelected((prev) => {
            if (prev.includes(id)) {
                return prev.filter((hid) => hid !== id)
            }
            sessionStorage.removeItem('userClearedSelection')
            return [...prev, id]
        })
    }

    const handleOk = () => {
        setSelectedHospitalIds(tempSelected)

        const selectedNames: Record<string, string> = {}
        tempSelected.forEach(id => {
            const hospital = hospitals.find(h => h.id === id)
            if (hospital) {
                selectedNames[id] = hospital.name
            }
        })
        sessionStorage.setItem('hospitalNames', JSON.stringify(selectedNames))
        sessionStorage.setItem('selectedHospitalIds', JSON.stringify(tempSelected))

        if (tempSelected.length === 0) {
            sessionStorage.setItem('userClearedSelection', 'true')
        } else {
            sessionStorage.removeItem('userClearedSelection')
        }

        setIsOpen(false)
    }

    const handleCancel = () => {
        setTempSelected([...selectedHospitalIds])
        setIsOpen(false)
    }

    const handleClearAll = () => {
        setTempSelected([])
        setSelectedHospitalIds([])
        sessionStorage.removeItem('hospitalNames')
        sessionStorage.removeItem('selectedHospitalIds')
    }

    const getHeaderText = () => {
        if (selectedHospitalIds.length === 0) return 'Select Hospital'
        if (selectedHospitalIds.length === 1) {
            const hospital = hospitals.find((h) => h.id === selectedHospitalIds[0])
            if (!hospital) return selectedHospitalIds[0]

            const nameHasId = hospital.name.startsWith(hospital.id)
            return nameHasId ? hospital.name : `${hospital.id}-${hospital.name}`
        }
        if (selectedHospitalIds.length <= 6) {
            return selectedHospitalIds.join(', ')
        }
        return `${selectedHospitalIds.length} hospitals selected`
    }

    const visibleSelectedCount = filteredHospitals.filter(h => tempSelected.includes(h.id)).length
    const allSelected = filteredHospitals.length > 0 && visibleSelectedCount === filteredHospitals.length
    const someSelected = visibleSelectedCount > 0 && visibleSelectedCount < filteredHospitals.length

    if (loading) {
        return <div className="hospital-selector loading">Loading hospitals...</div>
    }

    return (
        <div className="hospital-selector" ref={dropdownRef}>
            <div
                ref={headerRef}
                className={`dropdown-header ${isOpen ? 'open' : ''}`}
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    backgroundColor: 'white',
                    border: 'none',
                    padding: '0',
                    height: 'auto',
                    width: 'auto',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px'
                }}
            >
                {getHeaderText()}
                <svg
                    width="12"
                    height="7"
                    viewBox="0 0 12 7"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="dropdown-arrow"
                    style={{ marginLeft: '8px', transition: 'transform 0.3s ease' }}
                >
                    <path d="M1 1L6 6L11 1" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </div>

            {isOpen && (
                <div
                    className="dropdown-list show"
                    style={dropdownTop ? { top: `${dropdownTop}px` } : undefined}
                >
                    {hospitals.length > 4 && (
                    <input
                        type="text"
                        className="hospital-search-input"
                        placeholder="Search hospital ID or name..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value)
                            setHighlightedIndex(-1)
                        }}
                        onKeyDown={(e) => {
                            const total = filteredHospitals.length > 0 ? filteredHospitals.length + 1 : 0
                            if (e.key === 'ArrowDown') {
                                e.preventDefault()
                                if (total > 0) setHighlightedIndex(prev => (prev + 1) % total)
                            } else if (e.key === 'ArrowUp') {
                                e.preventDefault()
                                if (total > 0) setHighlightedIndex(prev => (prev - 1 + total) % total)
                            } else if (e.key === 'Enter') {
                                e.preventDefault()
                                if (highlightedIndex === 0) {
                                    handleToggleAll()
                                } else if (highlightedIndex > 0) {
                                    const hospital = filteredHospitals[highlightedIndex - 1]
                                    if (hospital) handleToggleHospital(hospital.id)
                                }
                            } else if (e.key === 'Escape') {
                                setIsOpen(false)
                            }
                        }}
                        autoFocus
                    />
                    )}

                    {filteredHospitals.length > 0 && (
                    <label className={`hospital-option ${highlightedIndex === 0 ? 'highlighted' : ''}`}>
                        <input
                            type="checkbox"
                            checked={allSelected}
                            ref={(el) => {
                                if (el) el.indeterminate = someSelected
                            }}
                            onChange={handleToggleAll}
                        />
                        All
                    </label>
                    )}

                    {filteredHospitals.map((hospital, index) => (
                        <label
                            key={hospital.id}
                            className={`hospital-option ${highlightedIndex === index + 1 ? 'highlighted' : ''}`}
                        >
                            <input
                                type="checkbox"
                                checked={tempSelected.includes(hospital.id)}
                                onChange={() => handleToggleHospital(hospital.id)}
                            />
                            {hospital.name}
                        </label>
                    ))}

                    {filteredHospitals.length === 0 && (
                        <div className="hospital-no-results">No hospitals found</div>
                    )}

                    <div className="dropdown-footer-buttons">
                        <button onClick={handleClearAll} className="btn-clear">
                            Clear All
                        </button>
                        <button onClick={handleOk} className="btn-ok">
                            OK
                        </button>
                        <button onClick={handleCancel} className="btn-cancel">
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

export default HospitalSelector
